import React, { useEffect, useRef, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as d3 from "d3";
import { TrendingUp, TrendingDown, Target, Award, Clock, BarChart3, Info } from "lucide-react";

/* ---------- Enhanced Win/Loss Pie Chart with Modern Design ---------- */
export const WinLossPieChart = ({ predictions, width = 320, height = 320 }) => {
  const { t } = useTranslation();
  const ref = useRef();
  const [hoveredSlice, setHoveredSlice] = useState(null);

  const chartData = useMemo(() => {
    if (!predictions || predictions.length === 0) return [];

    const won = predictions.filter(p => p.wasCorrectDirection === true).length;
    const lost = predictions.filter(p => p.outcome && p.wasCorrectDirection === false).length;
    const pending = predictions.filter(p => !p.outcome).length;
    const total = won + lost + pending;

    return [
      {
        label: t('credibility.analyticsCharts.winLoss.won'),
        value: won,
        percentage: total > 0 ? (won / total * 100).toFixed(1) : 0,
        color: "#00D9FF",
        gradient: ["#00D9FF", "#0099CC"],
        icon: "✓"
      },
      {
        label: t('credibility.analyticsCharts.winLoss.lost'),
        value: lost,
        percentage: total > 0 ? (lost / total * 100).toFixed(1) : 0,
        color: "#FF6B6B",
        gradient: ["#FF6B6B", "#CC3333"],
        icon: "✗"
      },
      {
        label: t('credibility.analyticsCharts.winLoss.pending'),
        value: pending,
        percentage: total > 0 ? (pending / total * 100).toFixed(1) : 0,
        color: "#FFD93D",
        gradient: ["#FFD93D", "#E6C200"],
        icon: "⏳"
      },
    ].filter(d => d.value > 0);
  }, [predictions, t]);

  useEffect(() => {
    if (chartData.length === 0) return;

    const radius = Math.min(width, height) / 2 - 40;
    const innerRadius = radius * 0.5;
    
    const arc = d3.arc()
      .innerRadius(innerRadius)
      .outerRadius(radius)
      .cornerRadius(8);
    
    const hoverArc = d3.arc()
      .innerRadius(innerRadius)
      .outerRadius(radius + 12)
      .cornerRadius(8);

    const pie = d3.pie()
      .value(d => d.value)
      .sort(null)
      .padAngle(0.02);

    d3.select(ref.current).selectAll("*").remove();
    
    const svg = d3.select(ref.current)
      .attr("width", width)
      .attr("height", height);

    // Add glow filter
    const defs = svg.append("defs");
    const filter = defs.append("filter")
      .attr("id", "glow")
      .attr("x", "-50%")
      .attr("y", "-50%")
      .attr("width", "200%")
      .attr("height", "200%");

    filter.append("feGaussianBlur")
      .attr("stdDeviation", "3")
      .attr("result", "coloredBlur");

    const feMerge = filter.append("feMerge");
    feMerge.append("feMergeNode").attr("in", "coloredBlur");
    feMerge.append("feMergeNode").attr("in", "SourceGraphic");

    // Create gradients
    chartData.forEach((d, i) => {
      const gradient = defs.append("linearGradient")
        .attr("id", `gradient-${i}`)
        .attr("gradientUnits", "userSpaceOnUse");
      
      gradient.append("stop")
        .attr("offset", "0%")
        .attr("stop-color", d.gradient[0]);
      
      gradient.append("stop")
        .attr("offset", "100%")
        .attr("stop-color", d.gradient[1]);
    });
      
    const g = svg.append("g")
      .attr("transform", `translate(${width / 2},${height / 2})`);

    const arcs = g.selectAll(".arc")
      .data(pie(chartData))
      .enter().append("g")
      .attr("class", "arc");

    // Add paths with enhanced hover effects
    arcs.append("path")
      .attr("d", arc)
      .attr("fill", (d, i) => `url(#gradient-${i})`)
      .attr("stroke", "#1a1a2e")
      .attr("stroke-width", 3)
      .attr("filter", "url(#glow)")
      .style("cursor", "pointer")
      .style("transition", "all 0.3s ease")
      .on("mouseover", function(event, d) {
        setHoveredSlice(d.data);
        d3.select(this)
          .transition()
          .duration(300)
          .ease(d3.easeBackOut.overshoot(1.2))
          .attr("d", hoverArc);
      })
      .on("mouseout", function() {
        setHoveredSlice(null);
        d3.select(this)
          .transition()
          .duration(300)
          .ease(d3.easeBackIn)
          .attr("d", arc);
      });

    // Add percentage labels with better styling
    arcs.append("text")
      .attr("transform", d => `translate(${arc.centroid(d)})`)
      .attr("dy", "0.35em")
      .style("text-anchor", "middle")
      .style("font-size", "16px")
      .style("font-weight", "700")
      .style("fill", "white")
      .style("text-shadow", "0 2px 4px rgba(0,0,0,0.8)")
      .text(d => d.data.value > 0 ? `${d.data.percentage}%` : '');

    // Enhanced center content
    const total = chartData.reduce((sum, d) => sum + d.value, 0);
    
    g.append("circle")
      .attr("r", innerRadius - 5)
      .attr("fill", "rgba(255,255,255,0.03)")
      .attr("stroke", "rgba(255,255,255,0.1)")
      .attr("stroke-width", 2);
    
    g.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "-0.5em")
      .style("font-size", "32px")
      .style("font-weight", "800")
      .style("fill", "#ffffff")
      .style("text-shadow", "0 2px 8px rgba(0,0,0,0.3)")
      .text(total);
      
    g.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "1em")
      .style("font-size", "14px")
      .style("font-weight", "500")
      .style("fill", "#a1a1aa")
      .text(t('credibility.analyticsCharts.winLoss.total'));

  }, [chartData, width, height, t]);

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Target className="w-8 h-8 text-purple-400" />
          </div>
          <p className="text-lg font-medium text-gray-300">{t('credibility.analyticsCharts.winLoss.emptyTitle')}</p>
          <p className="text-sm text-gray-500">{t('credibility.analyticsCharts.winLoss.emptySubtitle')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <svg ref={ref}></svg>
      
      {/* Enhanced Legend */}
      <div className="absolute -bottom-6 left-0 right-0 flex justify-center">
        <div className="flex space-x-6 bg-gray-800/60 backdrop-blur-sm rounded-2xl px-6 py-3 border border-gray-600/30">
          {chartData.map((item, index) => (
            <div key={index} className={`flex items-center space-x-2 transition-all duration-300 ${
              hoveredSlice?.label === item.label ? 'scale-110' : ''
            }`}>
              <div 
                className="w-3 h-3 rounded-full shadow-lg"
                style={{ 
                  backgroundColor: item.color,
                  boxShadow: `0 0 10px ${item.color}40`
                }}
              />
              <span className="text-sm font-medium text-gray-200">
                {item.label} <span className="text-gray-400">({item.value})</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ---------- Enhanced Horizon Bar Chart ---------- */
export const HorizonBarChart = ({ predictions, width = 380, height = 240 }) => {
  const { t } = useTranslation();
  const ref = useRef();
  const [hoveredBar, setHoveredBar] = useState(null);

  const chartData = useMemo(() => {
    if (!predictions || predictions.length === 0) return [];

    const buckets = { 
      short: { count: 0, won: 0, lost: 0 }, 
      medium: { count: 0, won: 0, lost: 0 }, 
      long: { count: 0, won: 0, lost: 0 } 
    };

    predictions.forEach(p => {
      const d = p.horizonDays || 0;
      let bucket;
      if (d <= 7) bucket = 'short';
      else if (d <= 30) bucket = 'medium';
      else bucket = 'long';

      buckets[bucket].count++;
      if (p.wasCorrectDirection === true) buckets[bucket].won++;
      else if (p.wasCorrectDirection === false) buckets[bucket].lost++;
    });

    return [
      {
        label: t('credibility.analyticsCharts.horizon.shortLabel'),
        fullLabel: t('credibility.analyticsCharts.horizon.shortFullLabel'),
        count: buckets.short.count,
        won: buckets.short.won,
        lost: buckets.short.lost,
        winRate: (buckets.short.won + buckets.short.lost) > 0 ?
          (buckets.short.won / (buckets.short.won + buckets.short.lost) * 100).toFixed(1) : 0,
        gradient: ["#00D9FF", "#0099CC"]
      },
      {
        label: t('credibility.analyticsCharts.horizon.mediumLabel'),
        fullLabel: t('credibility.analyticsCharts.horizon.mediumFullLabel'),
        count: buckets.medium.count,
        won: buckets.medium.won,
        lost: buckets.medium.lost,
        winRate: (buckets.medium.won + buckets.medium.lost) > 0 ?
          (buckets.medium.won / (buckets.medium.won + buckets.medium.lost) * 100).toFixed(1) : 0,
        gradient: ["#A855F7", "#7C3AED"]
      },
      {
        label: t('credibility.analyticsCharts.horizon.longLabel'),
        fullLabel: t('credibility.analyticsCharts.horizon.longFullLabel'),
        count: buckets.long.count,
        won: buckets.long.won,
        lost: buckets.long.lost,
        winRate: (buckets.long.won + buckets.long.lost) > 0 ?
          (buckets.long.won / (buckets.long.won + buckets.long.lost) * 100).toFixed(1) : 0,
        gradient: ["#F97316", "#EA580C"]
      },
    ];
  }, [predictions, t]);

  useEffect(() => {
    if (chartData.length === 0) return;

    const margin = { top: 30, right: 30, bottom: 60, left: 60 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    d3.select(ref.current).selectAll("*").remove();
    
    const svg = d3.select(ref.current)
      .attr("width", width)
      .attr("height", height);

    // Create gradients and filters
    const defs = svg.append("defs");
    
    // Glow filter
    const filter = defs.append("filter")
      .attr("id", "bar-glow")
      .attr("x", "-50%")
      .attr("y", "-50%")
      .attr("width", "200%")
      .attr("height", "200%");
    
    filter.append("feGaussianBlur")
      .attr("stdDeviation", "3")
      .attr("result", "coloredBlur");
    
    const feMerge = filter.append("feMerge");
    feMerge.append("feMergeNode").attr("in", "coloredBlur");
    feMerge.append("feMergeNode").attr("in", "SourceGraphic");

    // Create gradients
    chartData.forEach((d, i) => {
      const gradient = defs.append("linearGradient")
        .attr("id", `bar-gradient-${i}`)
        .attr("gradientUnits", "userSpaceOnUse")
        .attr("x1", 0).attr("y1", innerHeight)
        .attr("x2", 0).attr("y2", 0);

      gradient.append("stop")
        .attr("offset", "0%")
        .attr("stop-color", d.gradient[0])
        .attr("stop-opacity", 0.8);

      gradient.append("stop")
        .attr("offset", "100%")
        .attr("stop-color", d.gradient[1])
        .attr("stop-opacity", 1);
    });
      
    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const x = d3.scaleBand()
      .domain(chartData.map(d => d.label))
      .range([0, innerWidth])
      .padding(0.4);

    const y = d3.scaleLinear()
      .domain([0, d3.max(chartData, d => d.count) || 1])
      .range([innerHeight, 0]);

    // Add grid lines
    g.selectAll(".grid-line")
      .data(y.ticks(5))
      .enter().append("line")
      .attr("class", "grid-line")
      .attr("x1", 0)
      .attr("x2", innerWidth)
      .attr("y1", d => y(d))
      .attr("y2", d => y(d))
      .attr("stroke", "rgba(255,255,255,0.05)")
      .attr("stroke-width", 1);

    // Enhanced bars
    g.selectAll(".bar")
      .data(chartData)
      .enter().append("rect")
      .attr("class", "bar")
      .attr("x", d => x(d.label))
      .attr("y", d => y(d.count))
      .attr("width", x.bandwidth())
      .attr("height", d => innerHeight - y(d.count))
      .attr("fill", (d, i) => `url(#bar-gradient-${i})`)
      .attr("stroke", "rgba(255,255,255,0.2)")
      .attr("stroke-width", 2)
      .attr("rx", 8)
      .attr("ry", 8)
      .attr("filter", "url(#bar-glow)")
      .style("cursor", "pointer")
      .on("mouseover", function(event, d) {
        setHoveredBar(d);
        d3.select(this)
          .transition()
          .duration(200)
          .attr("y", y(d.count) - 5)
          .attr("height", (innerHeight - y(d.count)) + 5);
      })
      .on("mouseout", function(event, d) {
        setHoveredBar(null);
        d3.select(this)
          .transition()
          .duration(200)
          .attr("y", y(d.count))
          .attr("height", innerHeight - y(d.count));
      });

    // Enhanced labels
    g.selectAll(".count-label")
      .data(chartData)
      .enter().append("text")
      .attr("class", "count-label")
      .attr("x", d => x(d.label) + x.bandwidth() / 2)
      .attr("y", d => y(d.count) - 15)
      .attr("text-anchor", "middle")
      .style("fill", "white")
      .style("font-size", "14px")
      .style("font-weight", "700")
      .style("text-shadow", "0 2px 4px rgba(0,0,0,0.5)")
      .text(d => d.count);

    // Win rate labels inside bars
    g.selectAll(".winrate-label")
      .data(chartData.filter(d => d.won + d.lost > 0))
      .enter().append("text")
      .attr("class", "winrate-label")
      .attr("x", d => x(d.label) + x.bandwidth() / 2)
      .attr("y", d => y(d.count / 2) + 5)
      .attr("text-anchor", "middle")
      .style("fill", "white")
      .style("font-size", "12px")
      .style("font-weight", "600")
      .style("text-shadow", "0 1px 2px rgba(0,0,0,0.8)")
      .text(d => `${d.winRate}%`);

    // Enhanced axes
    const xAxis = g.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x));
    
    xAxis.selectAll("text")
      .style("fill", "#e5e7eb")
      .style("font-size", "13px")
      .style("font-weight", "600");
    
    xAxis.selectAll("path, line")
      .style("stroke", "rgba(255,255,255,0.2)");

    const yAxis = g.append("g")
      .call(d3.axisLeft(y).ticks(5));
    
    yAxis.selectAll("text")
      .style("fill", "#e5e7eb")
      .style("font-size", "12px");
    
    yAxis.selectAll("path, line")
      .style("stroke", "rgba(255,255,255,0.2)");

    // Y Axis label
    g.append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", -40)
      .attr("x", -innerHeight / 2)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .style("fill", "#d1d5db")
      .style("font-size", "13px")
      .style("font-weight", "500")
      .text(t('credibility.analyticsCharts.horizon.yAxisLabel'));

  }, [chartData, width, height, t]);

  if (chartData.every(d => d.count === 0)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-blue-400" />
          </div>
          <p className="text-lg font-medium text-gray-300">{t('credibility.analyticsCharts.horizon.emptyTitle')}</p>
          <p className="text-sm text-gray-500">{t('credibility.analyticsCharts.horizon.emptySubtitle')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <svg ref={ref}></svg>
      
      {/* Tooltip */}
      {hoveredBar && (
        <div className="absolute top-4 left-4 bg-gray-800/90 backdrop-blur-sm border border-gray-600/50 rounded-xl p-3 shadow-2xl">
          <p className="text-sm font-semibold text-white">{hoveredBar.fullLabel}</p>
          <p className="text-xs text-gray-300">
            {t('credibility.analyticsCharts.horizon.tooltipTotals', { total: hoveredBar.count, won: hoveredBar.won, lost: hoveredBar.lost })}
          </p>
          {hoveredBar.won + hoveredBar.lost > 0 && (
            <p className="text-xs text-emerald-400 font-medium">
              {t('credibility.analyticsCharts.horizon.tooltipWinRate', { rate: hoveredBar.winRate })}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/* ---------- Enhanced Performance Trend Chart ---------- */
export const PerformanceTrendChart = ({ predictions, width = 450, height = 280 }) => {
  const { t } = useTranslation();
  const ref = useRef();
  const [hoveredPoint, setHoveredPoint] = useState(null);

  const chartData = useMemo(() => {
    if (!predictions || predictions.length === 0) return [];

    const completedPredictions = predictions
      .filter(p => p.outcome && p.evaluatedAt)
      .sort((a, b) => new Date(a.evaluatedAt) - new Date(b.evaluatedAt));

    let runningWins = 0;
    let runningTotal = 0;

    return completedPredictions.map((p, index) => {
      runningTotal++;
      if (p.wasCorrectDirection) runningWins++;
      
      return {
        date: new Date(p.evaluatedAt),
        winRate: (runningWins / runningTotal) * 100,
        totalScore: p.totalScore || 0,
        actualReturn: p.actualReturn || 0,
        ticker: p.ticker || p.entityTicker,
        runningTotal,
        index
      };
    });
  }, [predictions]);

  useEffect(() => {
    if (chartData.length === 0) return;

    const margin = { top: 30, right: 70, bottom: 50, left: 70 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    d3.select(ref.current).selectAll("*").remove();
    
    const svg = d3.select(ref.current)
      .attr("width", width)
      .attr("height", height);

    // Create gradients and filters
    const defs = svg.append("defs");
    
    // Area gradient
    const areaGradient = defs.append("linearGradient")
      .attr("id", "area-gradient")
      .attr("gradientUnits", "userSpaceOnUse")
      .attr("x1", 0).attr("y1", innerHeight)
      .attr("x2", 0).attr("y2", 0);

    areaGradient.append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "#00D9FF")
      .attr("stop-opacity", 0.1);

    areaGradient.append("stop")
      .attr("offset", "50%")
      .attr("stop-color", "#00D9FF")
      .attr("stop-opacity", 0.3);

    areaGradient.append("stop")
      .attr("offset", "100%")
      .attr("stop-color", "#00D9FF")
      .attr("stop-opacity", 0.6);

    // Glow filter for line
    const glowFilter = defs.append("filter")
      .attr("id", "line-glow")
      .attr("x", "-50%")
      .attr("y", "-50%")
      .attr("width", "200%")
      .attr("height", "200%");

    glowFilter.append("feGaussianBlur")
      .attr("stdDeviation", "4")
      .attr("result", "coloredBlur");

    const feMerge = glowFilter.append("feMerge");
    feMerge.append("feMergeNode").attr("in", "coloredBlur");
    feMerge.append("feMergeNode").attr("in", "SourceGraphic");
      
    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Scales
    const x = d3.scaleTime()
      .domain(d3.extent(chartData, d => d.date))
      .range([0, innerWidth]);

    const y = d3.scaleLinear()
      .domain([0, 100])
      .range([innerHeight, 0]);

    // Add grid lines
    g.selectAll(".grid-line-y")
      .data(y.ticks(5))
      .enter().append("line")
      .attr("class", "grid-line-y")
      .attr("x1", 0)
      .attr("x2", innerWidth)
      .attr("y1", d => y(d))
      .attr("y2", d => y(d))
      .attr("stroke", "rgba(255,255,255,0.05)")
      .attr("stroke-width", 1);

    g.selectAll(".grid-line-x")
      .data(x.ticks(5))
      .enter().append("line")
      .attr("class", "grid-line-x")
      .attr("x1", d => x(d))
      .attr("x2", d => x(d))
      .attr("y1", 0)
      .attr("y2", innerHeight)
      .attr("stroke", "rgba(255,255,255,0.05)")
      .attr("stroke-width", 1);

    // Line and area generators
    const line = d3.line()
      .x(d => x(d.date))
      .y(d => y(d.winRate))
      .curve(d3.curveCardinal);

    const area = d3.area()
      .x(d => x(d.date))
      .y0(innerHeight)
      .y1(d => y(d.winRate))
      .curve(d3.curveCardinal);

    // Add area
    g.append("path")
      .datum(chartData)
      .attr("fill", "url(#area-gradient)")
      .attr("d", area);

    // Add 50% reference line with enhanced styling
    g.append("line")
      .attr("x1", 0)
      .attr("x2", innerWidth)
      .attr("y1", y(50))
      .attr("y2", y(50))
      .attr("stroke", "#FFD93D")
      .attr("stroke-dasharray", "8,4")
      .attr("stroke-width", 2)
      .attr("opacity", 0.7);

    g.append("text")
      .attr("x", innerWidth - 5)
      .attr("y", y(50) - 8)
      .attr("text-anchor", "end")
      .style("fill", "#FFD93D")
      .style("font-size", "12px")
      .style("font-weight", "600")
      .style("text-shadow", "0 1px 2px rgba(0,0,0,0.5)")
      .text(t('credibility.analyticsCharts.trend.breakeven'));

    // Add main line
    g.append("path")
      .datum(chartData)
      .attr("fill", "none")
      .attr("stroke", "#00D9FF")
      .attr("stroke-width", 4)
      .attr("filter", "url(#line-glow)")
      .attr("d", line);

    // Add enhanced dots
    g.selectAll(".dot")
      .data(chartData)
      .enter().append("circle")
      .attr("class", "dot")
      .attr("cx", d => x(d.date))
      .attr("cy", d => y(d.winRate))
      .attr("r", 6)
      .attr("fill", "#00D9FF")
      .attr("stroke", "#1a1a2e")
      .attr("stroke-width", 3)
      .style("cursor", "pointer")
      .style("filter", "drop-shadow(0 0 6px rgba(0, 217, 255, 0.6))")
      .on("mouseover", function(event, d) {
        setHoveredPoint(d);
        d3.select(this)
          .transition()
          .duration(200)
          .attr("r", 9)
          .attr("stroke-width", 4);
      })
      .on("mouseout", function() {
        setHoveredPoint(null);
        d3.select(this)
          .transition()
          .duration(200)
          .attr("r", 6)
          .attr("stroke-width", 3);
      });

    // Enhanced axes
    const xAxis = g.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x).ticks(6).tickFormat(d3.timeFormat("%m/%d")));
    
    xAxis.selectAll("text")
      .style("fill", "#e5e7eb")
      .style("font-size", "12px")
      .style("font-weight", "500");
    
    xAxis.selectAll("path, line")
      .style("stroke", "rgba(255,255,255,0.3)");

    const yAxis = g.append("g")
      .call(d3.axisLeft(y).ticks(6));
    
    yAxis.selectAll("text")
      .style("fill", "#e5e7eb")
      .style("font-size", "12px")
      .style("font-weight", "500");
    
    yAxis.selectAll("path, line")
      .style("stroke", "rgba(255,255,255,0.3)");

    // Axis labels
    g.append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", -45)
      .attr("x", -innerHeight / 2)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .style("fill", "#d1d5db")
      .style("font-size", "13px")
      .style("font-weight", "600")
      .text(t('credibility.analyticsCharts.trend.yAxisLabel'));

    g.append("text")
      .attr("transform", `translate(${innerWidth / 2}, ${innerHeight + 35})`)
      .style("text-anchor", "middle")
      .style("fill", "#d1d5db")
      .style("font-size", "13px")
      .style("font-weight", "600")
      .text(t('credibility.analyticsCharts.trend.xAxisLabel'));

  }, [chartData, width, height, t]);

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <TrendingUp className="w-8 h-8 text-cyan-400" />
          </div>
          <p className="text-lg font-medium text-gray-300">{t('credibility.analyticsCharts.trend.emptyTitle')}</p>
          <p className="text-sm text-gray-500">{t('credibility.analyticsCharts.trend.emptySubtitle')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <svg ref={ref}></svg>
      
      {/* Current rate indicator */}
      <div className="absolute top-4 right-4 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 backdrop-blur-sm border border-cyan-400/30 rounded-xl px-4 py-2">
        <div className="text-xs text-cyan-300 font-medium">{t('credibility.analyticsCharts.trend.currentWinRate')}</div>
        <div className="text-2xl font-bold text-white">
          {chartData[chartData.length - 1]?.winRate.toFixed(1)}%
        </div>
      </div>

      {/* Tooltip */}
      {hoveredPoint && (
        <div className="absolute top-16 left-4 bg-gray-900/95 backdrop-blur-sm border border-gray-600/50 rounded-xl p-4 shadow-2xl min-w-48">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-white">
              {hoveredPoint.date.toLocaleDateString()}
            </span>
            <span className="text-xs text-gray-400">#{hoveredPoint.index + 1}</span>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-cyan-400 font-medium">
              {t('credibility.analyticsCharts.trend.winRatePrefix', { rate: hoveredPoint.winRate.toFixed(1) })}
            </p>
            <p className="text-xs text-gray-300">
              {t('credibility.analyticsCharts.trend.totalPredictionsPrefix', { count: hoveredPoint.runningTotal })}
            </p>
            {hoveredPoint.ticker && (
              <p className="text-xs text-gray-300">
                {t('credibility.analyticsCharts.trend.tickerPrefix', { ticker: hoveredPoint.ticker })}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------- Enhanced Sector Performance Chart ---------- */
export const SectorPerformanceChart = ({ predictions, width = 400, height = 260 }) => {
  const { t } = useTranslation();
  const ref = useRef();
  const [hoveredBar, setHoveredBar] = useState(null);

  const chartData = useMemo(() => {
    if (!predictions || predictions.length === 0) return [];

    const sectorStats = {};
    
    predictions.forEach(p => {
      const sector = p.sector || 'Other';
      if (!sectorStats[sector]) {
        sectorStats[sector] = { total: 0, won: 0, totalScore: 0, count: 0 };
      }
      
      sectorStats[sector].total++;
      if (p.wasCorrectDirection === true) sectorStats[sector].won++;
      if (p.totalScore) {
        sectorStats[sector].totalScore += p.totalScore;
        sectorStats[sector].count++;
      }
    });

    return Object.entries(sectorStats)
      .map(([sector, stats]) => ({
        sector: sector.length > 12 ? sector.substring(0, 12) + '...' : sector,
        fullSector: sector,
        winRate: stats.total > 0 ? (stats.won / stats.total * 100) : 0,
        avgScore: stats.count > 0 ? (stats.totalScore / stats.count) : 0,
        total: stats.total,
        won: stats.won,
        lost: stats.total - stats.won
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);
  }, [predictions]);

  useEffect(() => {
    if (chartData.length === 0) return;

    const margin = { top: 30, right: 30, bottom: 90, left: 70 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    d3.select(ref.current).selectAll("*").remove();
    
    const svg = d3.select(ref.current)
      .attr("width", width)
      .attr("height", height);

    // Create gradients
    const defs = svg.append("defs");
    
    const colorScale = d3.scaleSequential()
      .domain([0, 100])
      .interpolator(t => {
        if (t < 0.5) return d3.interpolateReds(t * 2);
        return d3.interpolateGreens((t - 0.5) * 2);
      });

    chartData.forEach((d, i) => {
      const gradient = defs.append("linearGradient")
        .attr("id", `sector-gradient-${i}`)
        .attr("gradientUnits", "userSpaceOnUse")
        .attr("x1", 0).attr("y1", innerHeight)
        .attr("x2", 0).attr("y2", 0);

      const baseColor = colorScale(d.winRate);
      gradient.append("stop")
        .attr("offset", "0%")
        .attr("stop-color", d3.rgb(baseColor).darker(0.3))
        .attr("stop-opacity", 0.8);

      gradient.append("stop")
        .attr("offset", "100%")
        .attr("stop-color", baseColor)
        .attr("stop-opacity", 1);
    });

    // Glow filter
    const glowFilter = defs.append("filter")
      .attr("id", "sector-glow")
      .attr("x", "-50%")
      .attr("y", "-50%")
      .attr("width", "200%")
      .attr("height", "200%");

    glowFilter.append("feGaussianBlur")
      .attr("stdDeviation", "3")
      .attr("result", "coloredBlur");

    const feMerge = glowFilter.append("feMerge");
    feMerge.append("feMergeNode").attr("in", "coloredBlur");
    feMerge.append("feMergeNode").attr("in", "SourceGraphic");
      
    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const x = d3.scaleBand()
      .domain(chartData.map(d => d.sector))
      .range([0, innerWidth])
      .padding(0.3);

    const y = d3.scaleLinear()
      .domain([0, 100])
      .range([innerHeight, 0]);

    // Add grid lines
    g.selectAll(".grid-line")
      .data(y.ticks(5))
      .enter().append("line")
      .attr("class", "grid-line")
      .attr("x1", 0)
      .attr("x2", innerWidth)
      .attr("y1", d => y(d))
      .attr("y2", d => y(d))
      .attr("stroke", "rgba(255,255,255,0.05)")
      .attr("stroke-width", 1);

    // Enhanced bars
    g.selectAll(".bar")
      .data(chartData)
      .enter().append("rect")
      .attr("class", "bar")
      .attr("x", d => x(d.sector))
      .attr("y", d => y(d.winRate))
      .attr("width", x.bandwidth())
      .attr("height", d => innerHeight - y(d.winRate))
      .attr("fill", (d, i) => `url(#sector-gradient-${i})`)
      .attr("stroke", "rgba(255,255,255,0.3)")
      .attr("stroke-width", 2)
      .attr("rx", 6)
      .attr("ry", 6)
      .attr("filter", "url(#sector-glow)")
      .style("cursor", "pointer")
      .on("mouseover", function(event, d) {
        setHoveredBar(d);
        d3.select(this)
          .transition()
          .duration(200)
          .attr("y", y(d.winRate) - 3)
          .attr("height", (innerHeight - y(d.winRate)) + 3);
      })
      .on("mouseout", function(event, d) {
        setHoveredBar(null);
        d3.select(this)
          .transition()
          .duration(200)
          .attr("y", y(d.winRate))
          .attr("height", innerHeight - y(d.winRate));
      });

    // Win rate labels
    g.selectAll(".winrate-label")
      .data(chartData)
      .enter().append("text")
      .attr("class", "winrate-label")
      .attr("x", d => x(d.sector) + x.bandwidth() / 2)
      .attr("y", d => y(d.winRate) - 8)
      .attr("text-anchor", "middle")
      .style("fill", "white")
      .style("font-size", "12px")
      .style("font-weight", "700")
      .style("text-shadow", "0 2px 4px rgba(0,0,0,0.8)")
      .text(d => `${d.winRate.toFixed(0)}%`);

    // Count labels
    g.selectAll(".count-label")
      .data(chartData)
      .enter().append("text")
      .attr("class", "count-label")
      .attr("x", d => x(d.sector) + x.bandwidth() / 2)
      .attr("y", d => y(d.winRate / 2) + 4)
      .attr("text-anchor", "middle")
      .style("fill", "white")
      .style("font-size", "10px")
      .style("font-weight", "600")
      .style("text-shadow", "0 1px 2px rgba(0,0,0,0.8)")
      .text(d => `(${d.total})`);

    // Enhanced axes
    const xAxis = g.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x));
    
    xAxis.selectAll("text")
      .style("fill", "#e5e7eb")
      .style("font-size", "11px")
      .style("font-weight", "500")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end");
    
    xAxis.selectAll("path, line")
      .style("stroke", "rgba(255,255,255,0.3)");

    const yAxis = g.append("g")
      .call(d3.axisLeft(y).ticks(5));
    
    yAxis.selectAll("text")
      .style("fill", "#e5e7eb")
      .style("font-size", "12px")
      .style("font-weight", "500");
    
    yAxis.selectAll("path, line")
      .style("stroke", "rgba(255,255,255,0.3)");

    // Y Axis label
    g.append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", -45)
      .attr("x", -innerHeight / 2)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .style("fill", "#d1d5db")
      .style("font-size", "13px")
      .style("font-weight", "600")
      .text(t('credibility.analyticsCharts.sector.yAxisLabel'));

  }, [chartData, width, height, t]);

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-8 h-8 text-yellow-400" />
          </div>
          <p className="text-lg font-medium text-gray-300">{t('credibility.analyticsCharts.sector.emptyTitle')}</p>
          <p className="text-sm text-gray-500">{t('credibility.analyticsCharts.sector.emptySubtitle')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <svg ref={ref}></svg>
      
      {/* Tooltip */}
      {hoveredBar && (
        <div className="absolute top-4 right-4 bg-gray-900/95 backdrop-blur-sm border border-gray-600/50 rounded-xl p-4 shadow-2xl min-w-52">
          <p className="text-sm font-semibold text-white mb-2">{hoveredBar.fullSector}</p>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-300">{t('credibility.analyticsCharts.sector.tooltipWinRate')}</span>
              <span className="text-emerald-400 font-medium">{hoveredBar.winRate.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">{t('credibility.analyticsCharts.sector.tooltipTotal')}</span>
              <span className="text-white">{hoveredBar.total}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">{t('credibility.analyticsCharts.sector.tooltipWon')}</span>
              <span className="text-emerald-400">{hoveredBar.won}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">{t('credibility.analyticsCharts.sector.tooltipLost')}</span>
              <span className="text-rose-400">{hoveredBar.lost}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------- Enhanced Score Distribution Chart ---------- */
export const ScoreDistributionChart = ({ predictions, width = 340, height = 200 }) => {
  const { t } = useTranslation();
  const ref = useRef();
  const [hoveredBar, setHoveredBar] = useState(null);

  const chartData = useMemo(() => {
    if (!predictions || predictions.length === 0) return [];

    const scores = predictions
      .filter(p => p.totalScore !== undefined && p.totalScore !== null)
      .map(p => p.totalScore);

    if (scores.length === 0) return [];

    const bins = [
      { range: '0-20', min: 0, max: 20, count: 0, color: '#FF6B6B', gradient: ['#FF6B6B', '#CC3333'] },
      { range: '21-40', min: 21, max: 40, count: 0, color: '#FF9F43', gradient: ['#FF9F43', '#E67E22'] },
      { range: '41-60', min: 41, max: 60, count: 0, color: '#FFD93D', gradient: ['#FFD93D', '#F1C40F'] },
      { range: '61-80', min: 61, max: 80, count: 0, color: '#6BCF7F', gradient: ['#6BCF7F', '#27AE60'] },
      { range: '81-100', min: 81, max: 100, count: 0, color: '#00D9FF', gradient: ['#00D9FF', '#0099CC'] }
    ];

    scores.forEach(score => {
      const bin = bins.find(b => score >= b.min && score <= b.max);
      if (bin) bin.count++;
    });

    return bins.filter(b => b.count > 0);
  }, [predictions]);

  useEffect(() => {
    if (chartData.length === 0) return;

    const margin = { top: 30, right: 30, bottom: 50, left: 50 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    d3.select(ref.current).selectAll("*").remove();
    
    const svg = d3.select(ref.current)
      .attr("width", width)
      .attr("height", height);

    // Create gradients
    const defs = svg.append("defs");
    
    chartData.forEach((d, i) => {
      const gradient = defs.append("linearGradient")
        .attr("id", `score-gradient-${i}`)
        .attr("gradientUnits", "userSpaceOnUse")
        .attr("x1", 0).attr("y1", innerHeight)
        .attr("x2", 0).attr("y2", 0);

      gradient.append("stop")
        .attr("offset", "0%")
        .attr("stop-color", d.gradient[1])
        .attr("stop-opacity", 0.8);

      gradient.append("stop")
        .attr("offset", "100%")
        .attr("stop-color", d.gradient[0])
        .attr("stop-opacity", 1);
    });

    // Glow filter
    const glowFilter = defs.append("filter")
      .attr("id", "score-glow")
      .attr("x", "-50%")
      .attr("y", "-50%")
      .attr("width", "200%")
      .attr("height", "200%");

    glowFilter.append("feGaussianBlur")
      .attr("stdDeviation", "3")
      .attr("result", "coloredBlur");

    const feMerge = glowFilter.append("feMerge");
    feMerge.append("feMergeNode").attr("in", "coloredBlur");
    feMerge.append("feMergeNode").attr("in", "SourceGraphic");
      
    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const x = d3.scaleBand()
      .domain(chartData.map(d => d.range))
      .range([0, innerWidth])
      .padding(0.3);

    const y = d3.scaleLinear()
      .domain([0, d3.max(chartData, d => d.count) || 1])
      .range([innerHeight, 0]);

    // Add grid lines
    g.selectAll(".grid-line")
      .data(y.ticks(4))
      .enter().append("line")
      .attr("class", "grid-line")
      .attr("x1", 0)
      .attr("x2", innerWidth)
      .attr("y1", d => y(d))
      .attr("y2", d => y(d))
      .attr("stroke", "rgba(255,255,255,0.05)")
      .attr("stroke-width", 1);

    // Enhanced bars
    g.selectAll(".bar")
      .data(chartData)
      .enter().append("rect")
      .attr("class", "bar")
      .attr("x", d => x(d.range))
      .attr("y", d => y(d.count))
      .attr("width", x.bandwidth())
      .attr("height", d => innerHeight - y(d.count))
      .attr("fill", (d, i) => `url(#score-gradient-${i})`)
      .attr("stroke", "rgba(255,255,255,0.3)")
      .attr("stroke-width", 2)
      .attr("rx", 6)
      .attr("ry", 6)
      .attr("filter", "url(#score-glow)")
      .style("cursor", "pointer")
      .on("mouseover", function(event, d) {
        setHoveredBar(d);
        d3.select(this)
          .transition()
          .duration(200)
          .attr("y", y(d.count) - 3)
          .attr("height", (innerHeight - y(d.count)) + 3);
      })
      .on("mouseout", function(event, d) {
        setHoveredBar(null);
        d3.select(this)
          .transition()
          .duration(200)
          .attr("y", y(d.count))
          .attr("height", innerHeight - y(d.count));
      });

    // Count labels
    g.selectAll(".count-label")
      .data(chartData)
      .enter().append("text")
      .attr("class", "count-label")
      .attr("x", d => x(d.range) + x.bandwidth() / 2)
      .attr("y", d => y(d.count) - 8)
      .attr("text-anchor", "middle")
      .style("fill", "white")
      .style("font-size", "13px")
      .style("font-weight", "700")
      .style("text-shadow", "0 2px 4px rgba(0,0,0,0.8)")
      .text(d => d.count);

    // Enhanced axes
    const xAxis = g.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x));
    
    xAxis.selectAll("text")
      .style("fill", "#e5e7eb")
      .style("font-size", "12px")
      .style("font-weight", "500");
    
    xAxis.selectAll("path, line")
      .style("stroke", "rgba(255,255,255,0.3)");

    const yAxis = g.append("g")
      .call(d3.axisLeft(y).ticks(4));
    
    yAxis.selectAll("text")
      .style("fill", "#e5e7eb")
      .style("font-size", "11px")
      .style("font-weight", "500");
    
    yAxis.selectAll("path, line")
      .style("stroke", "rgba(255,255,255,0.3)");

    // Axis labels
    g.append("text")
      .attr("transform", `translate(${innerWidth / 2}, ${innerHeight + 35})`)
      .style("text-anchor", "middle")
      .style("fill", "#d1d5db")
      .style("font-size", "12px")
      .style("font-weight", "600")
      .text(t('credibility.analyticsCharts.scoreDist.xAxisLabel'));

    g.append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", -35)
      .attr("x", -innerHeight / 2)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .style("fill", "#d1d5db")
      .style("font-size", "12px")
      .style("font-weight", "600")
      .text(t('credibility.analyticsCharts.scoreDist.yAxisLabel'));

  }, [chartData, width, height, t]);

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Award className="w-8 h-8 text-purple-400" />
          </div>
          <p className="text-lg font-medium text-gray-300">{t('credibility.analyticsCharts.scoreDist.emptyTitle')}</p>
          <p className="text-sm text-gray-500">{t('credibility.analyticsCharts.scoreDist.emptySubtitle')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <svg ref={ref}></svg>
      
      {/* Tooltip */}
      {hoveredBar && (
        <div className="absolute top-4 right-4 bg-gray-900/95 backdrop-blur-sm border border-gray-600/50 rounded-xl p-3 shadow-2xl">
          <p className="text-sm font-semibold text-white">{t('credibility.analyticsCharts.scoreDist.tooltipRange', { range: hoveredBar.range })}</p>
          <p className="text-xs text-gray-300">{t('credibility.analyticsCharts.scoreDist.tooltipCount', { count: hoveredBar.count })}</p>
        </div>
      )}
    </div>
  );
};

/* ---------- Enhanced Analytics Dashboard Component ---------- */
export const AnalyticsDashboard = ({ predictions = [], className = "" }) => {
  const { t } = useTranslation();
  const stats = useMemo(() => {
    const completed = predictions.filter(p => p.outcome);
    const won = predictions.filter(p => p.wasCorrectDirection === true);
    const lost = predictions.filter(p => p.wasCorrectDirection === false);
    const pending = predictions.filter(p => !p.outcome);
    
    const winRate = completed.length > 0 ? (won.length / completed.length * 100) : 0;
    const avgScore = completed.length > 0 ? 
      completed.reduce((sum, p) => sum + (p.totalScore || 0), 0) / completed.length : 0;
    const avgReturn = completed.length > 0 ?
      completed.reduce((sum, p) => sum + (p.actualReturn || 0), 0) / completed.length : 0;

    return {
      total: predictions.length,
      completed: completed.length,
      won: won.length,
      lost: lost.length,
      pending: pending.length,
      winRate,
      avgScore,
      avgReturn
    };
  }, [predictions]);

  if (!predictions || predictions.length === 0) {
    return (
      <div className={`bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-700/50 p-12 ${className}`}>
        <div className="text-center">
          <div className="w-24 h-24 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-3xl flex items-center justify-center mx-auto mb-6">
            <BarChart3 className="w-12 h-12 text-purple-400" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-3">{t('credibility.analyticsCharts.dashboard.emptyTitle')}</h3>
          <p className="text-gray-400 text-lg max-w-md mx-auto">
            {t('credibility.analyticsCharts.dashboard.emptySubtitle')}
          </p>
          <div className="mt-6 flex justify-center space-x-8 text-sm text-gray-500">
            <div className="flex items-center">
              <Target className="w-4 h-4 mr-2" />
              {t('credibility.analyticsCharts.dashboard.winLossTracking')}
            </div>
            <div className="flex items-center">
              <TrendingUp className="w-4 h-4 mr-2" />
              {t('credibility.analyticsCharts.dashboard.performanceTrends')}
            </div>
            <div className="flex items-center">
              <Award className="w-4 h-4 mr-2" />
              {t('credibility.analyticsCharts.dashboard.scoreAnalysis')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-8 ${className}`}>
      {/* Enhanced Stats Overview */}
      <div className="bg-gradient-to-br from-gray-900/90 to-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-700/50 p-8">
        <div className="flex items-center mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center mr-4">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-white">{t('credibility.analyticsCharts.dashboard.performanceOverview')}</h3>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="group bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-2xl p-6 border border-blue-400/30 hover:border-blue-400/50 transition-all duration-300 hover:scale-105">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-300 text-sm font-semibold uppercase tracking-wide">{t('credibility.analyticsCharts.dashboard.totalPredictions')}</p>
                <p className="text-3xl font-bold text-white mt-1">{stats.total}</p>
                <div className="flex items-center mt-2 text-xs text-blue-400">
                  <Info className="w-3 h-3 mr-1" />
                  {t('credibility.analyticsCharts.dashboard.allTime')}
                </div>
              </div>
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </div>

          <div className="group bg-gradient-to-br from-emerald-600/20 to-green-600/20 rounded-2xl p-6 border border-emerald-400/30 hover:border-emerald-400/50 transition-all duration-300 hover:scale-105">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-300 text-sm font-semibold uppercase tracking-wide">{t('credibility.analyticsCharts.dashboard.winRate')}</p>
                <p className="text-3xl font-bold text-white mt-1">{stats.winRate.toFixed(1)}%</p>
                <div className="flex items-center mt-2 text-xs text-emerald-400">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full mr-2"></span>
                  {stats.won}W / {stats.lost}L
                </div>
              </div>
              <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                <Award className="w-6 h-6 text-emerald-400" />
              </div>
            </div>
          </div>

          <div className="group bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-2xl p-6 border border-purple-400/30 hover:border-purple-400/50 transition-all duration-300 hover:scale-105">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-300 text-sm font-semibold uppercase tracking-wide">{t('credibility.analyticsCharts.dashboard.avgScore')}</p>
                <p className="text-3xl font-bold text-white mt-1">{stats.avgScore.toFixed(0)}</p>
                <div className="flex items-center mt-2 text-xs text-purple-400">
                  <span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>
                  {t('credibility.analyticsCharts.dashboard.outOf100')}
                </div>
              </div>
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </div>

          <div className="group bg-gradient-to-br from-orange-600/20 to-red-600/20 rounded-2xl p-6 border border-orange-400/30 hover:border-orange-400/50 transition-all duration-300 hover:scale-105">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-300 text-sm font-semibold uppercase tracking-wide">{t('credibility.analyticsCharts.dashboard.avgReturn')}</p>
                <p className={`text-3xl font-bold mt-1 ${stats.avgReturn >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {stats.avgReturn >= 0 ? '+' : ''}{stats.avgReturn.toFixed(1)}%
                </p>
                <div className="flex items-center mt-2 text-xs text-orange-400">
                  <span className={`w-2 h-2 rounded-full mr-2 ${stats.avgReturn >= 0 ? 'bg-emerald-400' : 'bg-rose-400'}`}></span>
                  {t('credibility.analyticsCharts.dashboard.perPrediction')}
                </div>
              </div>
              <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
                {stats.avgReturn >= 0 ? 
                  <TrendingUp className="w-6 h-6 text-emerald-400" /> : 
                  <TrendingDown className="w-6 h-6 text-rose-400" />
                }
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Charts Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        {/* Win/Loss Distribution */}
        <div className="bg-gradient-to-br from-gray-900/90 to-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-700/50 p-8">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-xl font-bold text-white flex items-center">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl flex items-center justify-center mr-3">
                <Target className="w-4 h-4 text-white" />
              </div>
              {t('credibility.analyticsCharts.dashboard.winLossDistribution')}
            </h4>
            <div className="text-xs text-gray-400 bg-gray-800/50 rounded-lg px-3 py-1">
              {t('credibility.analyticsCharts.dashboard.totalCount', { count: stats.total })}
            </div>
          </div>
          <div className="flex justify-center">
            <WinLossPieChart predictions={predictions} />
          </div>
        </div>

        {/* Time Horizon Analysis */}
        <div className="bg-gradient-to-br from-gray-900/90 to-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-700/50 p-8">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-xl font-bold text-white flex items-center">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center mr-3">
                <Clock className="w-4 h-4 text-white" />
              </div>
              {t('credibility.analyticsCharts.dashboard.predictionHorizons')}
            </h4>
            <div className="text-xs text-gray-400 bg-gray-800/50 rounded-lg px-3 py-1">
              {t('credibility.analyticsCharts.dashboard.timeAnalysis')}
            </div>
          </div>
          <HorizonBarChart predictions={predictions} />
        </div>
      </div>

      {/* Performance Trend - Full Width */}
      <div className="bg-gradient-to-br from-gray-900/90 to-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-700/50 p-8">
        <div className="flex items-center justify-between mb-6">
          <h4 className="text-xl font-bold text-white flex items-center">
            <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center mr-3">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            {t('credibility.analyticsCharts.dashboard.winRateTrendOverTime')}
          </h4>
          <div className="text-xs text-gray-400 bg-gray-800/50 rounded-lg px-3 py-1">
            {t('credibility.analyticsCharts.dashboard.evaluatedCount', { count: stats.completed })}
          </div>
        </div>
        <PerformanceTrendChart predictions={predictions} width={800} height={300} />
      </div>

      {/* Bottom Charts Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        {/* Score Distribution */}
        <div className="bg-gradient-to-br from-gray-900/90 to-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-700/50 p-8">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-xl font-bold text-white flex items-center">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mr-3">
                <Award className="w-4 h-4 text-white" />
              </div>
              {t('credibility.analyticsCharts.dashboard.scoreDistribution')}
            </h4>
            <div className="text-xs text-gray-400 bg-gray-800/50 rounded-lg px-3 py-1">
              {t('credibility.analyticsCharts.dashboard.qualityMetrics')}
            </div>
          </div>
          <ScoreDistributionChart predictions={predictions} />
        </div>

        {/* Key Insights */}
        <div className="bg-gradient-to-br from-gray-900/90 to-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-700/50 p-8">
          <div className="flex items-center mb-6">
            <div className="w-8 h-8 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center mr-3">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            <h4 className="text-xl font-bold text-white">{t('credibility.analyticsCharts.dashboard.keyInsights')}</h4>
          </div>

          <div className="space-y-4">
            <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-2xl p-4">
              <h5 className="text-sm font-semibold text-cyan-300 mb-2 flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                {t('credibility.analyticsCharts.dashboard.bestPerformingTimeframe')}
              </h5>
              <div className="text-lg font-bold text-white">
                {(() => {
                  const shortTerm = predictions.filter(p => p.horizonDays <= 7 && p.outcome);
                  const mediumTerm = predictions.filter(p => p.horizonDays > 7 && p.horizonDays <= 30 && p.outcome);
                  const longTerm = predictions.filter(p => p.horizonDays > 30 && p.outcome);
                  
                  const shortWinRate = shortTerm.length > 0 ? shortTerm.filter(p => p.wasCorrectDirection).length / shortTerm.length * 100 : 0;
                  const mediumWinRate = mediumTerm.length > 0 ? mediumTerm.filter(p => p.wasCorrectDirection).length / mediumTerm.length * 100 : 0;
                  const longWinRate = longTerm.length > 0 ? longTerm.filter(p => p.wasCorrectDirection).length / longTerm.length * 100 : 0;
                  
                  const max = Math.max(shortWinRate, mediumWinRate, longWinRate);
                  if (max === shortWinRate && shortWinRate > 0) return t('credibility.analyticsCharts.dashboard.shortTermPct', { pct: shortWinRate.toFixed(0) });
                  if (max === mediumWinRate && mediumWinRate > 0) return t('credibility.analyticsCharts.dashboard.mediumTermPct', { pct: mediumWinRate.toFixed(0) });
                  if (max === longWinRate && longWinRate > 0) return t('credibility.analyticsCharts.dashboard.longTermPct', { pct: longWinRate.toFixed(0) });
                  return t('credibility.analyticsCharts.dashboard.noDataYet');
                })()}
              </div>
              <p className="text-xs text-gray-400 mt-1">{t('credibility.analyticsCharts.dashboard.highestSuccessRateHint')}</p>
            </div>

            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl p-4">
              <h5 className="text-sm font-semibold text-purple-300 mb-2 flex items-center">
                <BarChart3 className="w-4 h-4 mr-2" />
                {t('credibility.analyticsCharts.dashboard.mostActiveSector')}
              </h5>
              <div className="text-lg font-bold text-white">
                {(() => {
                  const sectorCounts = {};
                  predictions.forEach(p => {
                    const sector = p.sector || 'Other';
                    sectorCounts[sector] = (sectorCounts[sector] || 0) + 1;
                  });
                  const topSector = Object.entries(sectorCounts).sort((a, b) => b[1] - a[1])[0];
                  return topSector ? t('credibility.analyticsCharts.dashboard.sectorCount', { sector: topSector[0], count: topSector[1] }) : t('credibility.analyticsCharts.dashboard.noDataYet');
                })()}
              </div>
              <p className="text-xs text-gray-400 mt-1">{t('credibility.analyticsCharts.dashboard.mostPredictionsInSectorHint')}</p>
            </div>

            <div className="bg-gradient-to-r from-emerald-500/10 to-green-500/10 border border-emerald-500/20 rounded-2xl p-4">
              <h5 className="text-sm font-semibold text-emerald-300 mb-2 flex items-center">
                <Award className="w-4 h-4 mr-2" />
                {t('credibility.analyticsCharts.dashboard.recentStreak')}
              </h5>
              <div className="text-lg font-bold text-white">
                {(() => {
                  const recent = predictions
                    .filter(p => p.outcome)
                    .sort((a, b) => new Date(b.evaluatedAt) - new Date(a.evaluatedAt))
                    .slice(0, 10);

                  let currentStreak = 0;
                  let lastResult = null;

                  for (const p of recent) {
                    if (lastResult === null) {
                      lastResult = p.wasCorrectDirection;
                      currentStreak = 1;
                    } else if (p.wasCorrectDirection === lastResult) {
                      currentStreak++;
                    } else {
                      break;
                    }
                  }

                  if (currentStreak === 0) return t('credibility.analyticsCharts.dashboard.noRecentData');
                  return lastResult
                    ? t('credibility.analyticsCharts.dashboard.streakWins', { count: currentStreak })
                    : t('credibility.analyticsCharts.dashboard.streakLosses', { count: currentStreak });
                })()}
              </div>
              <p className="text-xs text-gray-400 mt-1">{t('credibility.analyticsCharts.dashboard.currentStreakHint')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sector Performance - Full Width */}
      <div className="bg-gradient-to-br from-gray-900/90 to-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-700/50 p-8">
        <div className="flex items-center justify-between mb-6">
          <h4 className="text-xl font-bold text-white flex items-center">
            <div className="w-8 h-8 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center mr-3">
              <BarChart3 className="w-4 h-4 text-white" />
            </div>
            {t('credibility.analyticsCharts.dashboard.sectorPerformanceAnalysis')}
          </h4>
          <div className="text-xs text-gray-400 bg-gray-800/50 rounded-lg px-3 py-1">
            {t('credibility.analyticsCharts.dashboard.winRatesBySector')}
          </div>
        </div>
        <div className="flex justify-center">
          <SectorPerformanceChart predictions={predictions} width={700} height={280} />
        </div>
      </div>

      {/* Performance Summary Footer */}
      <div className="bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-pink-600/10 border border-blue-500/20 rounded-3xl p-6">
        <div className="text-center">
          <h5 className="text-lg font-bold text-white mb-2">
            {t('credibility.analyticsCharts.dashboard.journeyTitle')}
          </h5>
          <p className="text-gray-300 text-sm max-w-2xl mx-auto">
            {stats.total > 0 ? (
              <>
                {t('credibility.analyticsCharts.dashboard.journeySummary', { total: stats.total, winRate: stats.winRate.toFixed(1) })}{' '}
                {stats.pending > 0 ? t('credibility.analyticsCharts.dashboard.journeyPendingSuffix', { count: stats.pending }) + ' ' : ''}
                {stats.avgReturn !== 0 ? t('credibility.analyticsCharts.dashboard.journeyAvgReturnSuffix', { avgReturn: stats.avgReturn.toFixed(1) }) : ''}
              </>
            ) : (
              t('credibility.analyticsCharts.dashboard.journeyEmpty')
            )}
          </p>
        </div>
      </div>
    </div>
  );
};